import { warmPdfjs } from 'pdf-core/engines/pdfjs-handle';
import { SessionStore } from 'pdf-model';
import { loadLocale } from 'pdf-shared';
import { getStoredLocale } from 'pdf-ui/ui';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { deviceTier } from './features/core/document';
import { flushDrafts } from './features/persistence/flush-drafts';
import { loadEditor } from './features/shell/editor-store';
import { RootBoundary } from './recovery';
import { initServiceWorkerUpdates } from './serviceWorkerUpdate';
import './app.css';

const container = document.getElementById('root');
if (container === null) throw new Error('#root is missing from index.html');

// One session store for the whole app. React subscribes to it; nothing
// else owns document state.
const store = new SessionStore();

// The interface language's catalogue is a chunk of its own (`pdf-shared` i18n): it is
// fetched before the first render, so the first frame is already in that language and
// the other catalogue is never downloaded.
//
// The app is wrapped in the root error boundary (`recovery.tsx`): a lazy chunk that cannot be
// fetched, or any render that throws, ends in a recovery screen with a reload button instead of
// a white page. The shell's autosave dies with it, so the drafts are written once more first.
const render = () =>
  createRoot(container).render(
    <StrictMode>
      <RootBoundary onCrash={() => void flushDrafts(store, deviceTier())}>
        <App store={store} />
      </RootBoundary>
    </StrictMode>,
  );
loadLocale(getStoredLocale()).then(render, render);

// The engine chunk is lazy for the sake of first paint; warming it while the browser is
// idle means the *first* file the user picks does not wait for the download and parse.
// It is the same same-origin chunk the open path would fetch - no new request appears
// that a cold open would not have made.
//
// The editor layout (`features/shell/editor.ts`: the docks, the canvas and the tool strip an
// open document shows) is warmed the same way, so opening a file never waits on its chunk.
//
// The two surfaces the shell reaches through a dynamic boundary (`pdf-ui/printing`,
// `pdf-ui/palette`) are warmed the same way and for the same reason: they are lazy so
// the first-paint JavaScript stays inside the ≤250 KiB budget, and prefetching them
// on idle keeps the first `Ctrl+P` or `Ctrl+K` from being a visible wait.
//
// Not while offline: a chunk the cache does not hold fails then, and Chromium keeps a
// failed dynamic import for the page's lifetime, so a warm-up run offline would leave the
// first open failing after the network is back. It runs when the network returns instead.
const warmChunks = () => {
  warmPdfjs();
  void loadEditor().catch(() => undefined);
  void import('pdf-ui/printing').catch(() => undefined);
  void import('pdf-ui/palette').catch(() => undefined);
};
const warm = () => {
  if (navigator.onLine) warmChunks();
  else window.addEventListener('online', warmChunks, { once: true });
  initServiceWorkerUpdates();
};
if ('requestIdleCallback' in window) {
  window.requestIdleCallback(warm, { timeout: 3000 });
} else {
  setTimeout(warm, 2000);
}
